import React from 'react';
import { cn } from '../../lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  prefixText?: string;
  suffixIcon?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, prefixText, suffixIcon, id, ...props }, ref) => {
    const generatedId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label htmlFor={generatedId} className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
            {label}
            {props.required && <span className="text-orange-400 ml-1">*</span>}
          </label>
        )}
        <div className="relative flex items-center">
          {prefixText && (
            <span className="inline-flex items-center px-3 py-2 text-sm text-slate-400 bg-slate-900 border border-r-0 border-slate-700 rounded-l-xl select-none">
              {prefixText}
            </span>
          )}
          <input
            id={generatedId}
            ref={ref}
            className={cn(
              'w-full px-3.5 py-2 text-sm bg-slate-900/90 text-slate-100 placeholder:text-slate-500 border border-slate-700 transition-all duration-150',
              prefixText ? 'rounded-r-xl' : 'rounded-xl',
              suffixIcon ? 'pr-10' : '',
              error
                ? 'border-rose-500/80 focus:border-rose-500 focus:ring-1 focus:ring-rose-500'
                : 'focus:border-orange-500/80 focus:ring-1 focus:ring-orange-500/50',
              'focus:outline-none',
              className
            )}
            {...props}
          />
          {suffixIcon && (
            <div className="absolute right-3 flex items-center pointer-events-none text-slate-400">
              {suffixIcon}
            </div>
          )}
        </div>
        {error && <p className="text-xs text-rose-400 font-medium">{error}</p>}
        {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
